import { rootRoute } from "@/routes";
import { Footer, NavBar } from "@/shared/ui/composites";
import { VStack } from "@/shared/ui/primitives";
import { createRoute, notFound } from "@tanstack/react-router";
import { useState, useCallback, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
	"pdfjs-dist/build/pdf.worker.min.mjs",
	import.meta.url,
).toString();

const LEGAL_DOCS = {
	terms: { title: "이용약관", file: "/terms.pdf" },
	privacy: { title: "개인정보처리방침", file: "/privacy.pdf" },
	refund: { title: "환불정책", file: "/refund.pdf" },
} as const;

type LegalType = keyof typeof LEGAL_DOCS;

const LegalPage = () => {
	const { type } = legalRoute.useParams();
	const doc = LEGAL_DOCS[type as LegalType];
	const [numPages, setNumPages] = useState<number>(0);
	const [pageWidth, setPageWidth] = useState<number>(800);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const observer = new ResizeObserver(([entry]) => {
			setPageWidth(entry.contentRect.width);
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const onLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
		setNumPages(numPages);
	}, []);

	return (
		<VStack className="w-full h-screen bg-bg-weak">
			<NavBar />
			<VStack className="flex-1 w-full overflow-y-auto justify-start items-center">
				<div ref={containerRef} className="w-full max-w-[800px] py-[100px]">
					<Document file={doc.file} onLoadSuccess={onLoadSuccess}>
						{Array.from({ length: numPages }, (_, i) => (
							<Page key={i} pageNumber={i + 1} width={pageWidth} />
						))}
					</Document>
				</div>
				<Footer />
			</VStack>
		</VStack>
	);
};

export const legalRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/legal/$type",
	component: LegalPage,
	beforeLoad: ({ params }) => {
		if (!(params.type in LEGAL_DOCS)) {
			throw notFound();
		}
	},
});
